import React from 'react';
import { Button, Collapse, Input, Select } from 'antd';

import { optionSearchFilter } from '../Input';
import { CALCULATOR, CLOSE, MATH_FUNCTION, PLUS } from '../icons';
import FormulaEditor, { FormulaReference } from '../common/FormulaEditor';
import ToggleRow from '../common/ToggleRow';
import { CURRENCY_LIST, getCurrency } from '../../utils/currency';
import { unitsOf } from '../../utils/units';
import { groupUnitOptions } from '../../utils/measurements';
import { LIMITS } from '../../utils/analytics';
import BuilderStep from './BuilderStep';
import { Field, SegmentedField } from './controls';
import ShowAsText from '../common/ShowAsText';
import FieldMessage, { explainedOptions, messageProps } from './FieldMessage';

const BLANKS_OPTIONS = [
	{ label: 'Leave out', value: 'omit' },
	{ label: 'Count as zero', value: 'zero' },
];

// A formula that reads the clock is worked out when the chart runs, in UTC
// (keepr-api SCHEMA "Formulas in a run" › Dates).
const CLOCK_HINT = "It reads the clock each time the chart runs. A formula's dates are in UTC, so its today() can be a day off from the collection's.";

// A measure the person hid while no formula reads it: drawn until one does
// again, when it is hidden again by itself (the hide is theirs, kept).
const SHOWN_WHILE_UNREAD = 'Shown on the chart while no formula reads it.';

/** The props the editable region takes from its messages: described by them, marked invalid under a refusal. */
function editorMessageProps(messages, id) {
	const list = Array.isArray(messages) ? messages : [];
	return {
		describedBy: list.some((m) => m && m.text) ? id : undefined,
		invalid: list.some((m) => m && m.tone === 'error'),
	};
}

/** "Show on the chart": offered on a measure a formula reads (d36), off to work it out for the formula alone. */
function HiddenToggle({ id, m, onMeasure, index, messages }) {
	return (
		<>
			<ToggleRow
				id={id}
				label='Show on the chart'
				description={m.hidden ? 'Off: worked out only for the formulas that read it.' : 'A formula reads it. Turn it off to work it out only for the formula.'}
				checked={!m.hidden}
				onChange={(on) => onMeasure(index, { hidden: !on, hideWish: !on })}
			/>
			<FieldMessage messages={messages} id={`${id}-messages`} />
		</>
	);
}

/** A formula field: the platform's formula editor, its messages, and what a finished formula answers. */
function FormulaField({ id, label, value, onChange, tokens, placeholder, hint, messages, status, clock }) {
	const messagesId = `${id}-messages`;
	return (
		<div className='chart-builder__field chart-builder__formula-field' data-field={id}>
			<span className='chart-builder__label'>{label}</span>
			<FormulaEditor
				value={value}
				onChange={onChange}
				tokens={tokens}
				ariaLabel={label}
				placeholder={placeholder}
				pickerId={`${id}-function`}
				hint={hint}
				{...editorMessageProps(messages, messagesId)}
			/>
			<FieldMessage messages={messages} id={messagesId} />
			{status ? <div className='chart-builder__hint'>{status}</div> : null}
			{clock ? <div className='chart-builder__hint'>{CLOCK_HINT}</div> : null}
		</div>
	);
}

/** The units a chart may show a measurement in: its measure's, no composites (an axis reads one number). */
function chartUnitOptions(measure) {
	return groupUnitOptions(unitsOf(measure).filter((unit) => !unit.parts));
}

function currencyOptions(codes) {
	const list = Array.isArray(codes) && codes.length ? codes.map(getCurrency).filter(Boolean) : CURRENCY_LIST.filter((c) => !c.fund);
	return list.map((c) => ({ value: c.code, label: `${c.code} · ${c.name}`, searchText: `${c.code} ${c.name}` }));
}

/**
 * Step 2, Measure: what the chart computes, as a sentence — a verb ("Total
 * of…") and, for every verb but **Count items**, the element it reads. Only
 * what the capability table allows is offered; what it refuses is listed
 * disabled with the sentence the server would refuse it with (plan §6.2).
 * Under an element measure: what an item with no value does (ruled d05),
 * the unit a measurement is shown in, the currency an amount that may hold
 * several is shown in, and the measure's own label.
 *
 * At the foot (charts A8, plan §6.2): **Calculate per item…** adds a measure
 * worked out from a formula over each item's elements, in the platform's
 * formula editor — its result type decides the verbs offered, through the
 * capability table, as an element of that type would (text can only be
 * counted); blanks follow the measure's own setting (d05). **Add formula
 * measure** adds a formula across the chart's other measures, worked out per
 * bucket on the server, the measures shown as pills by their labels. Every
 * refusal the server can answer is said beside its formula, in the server's
 * words, before any request (formulaModel). A measure a formula reads may be
 * hidden: worked out for the formula, not drawn (d36).
 *
 * A measure in a shape the builder does not know is shown as it is and kept.
 */
export default function MeasureStep({ draft, view, onMeasure, onAdd, onCalculate, onAddFormula, onRemove, messagesFor, step }) {
	const many = draft.measures.length > 1;
	const perItemRows = view.measures.filter((row) => row.kind === 'perItem');
	const formulaRows = view.measures.filter((row) => row.kind === 'formula');
	const ordinal = (rows, row) => (rows.length > 1 ? ` ${rows.indexOf(row) + 1}` : '');
	const footReasons = [...new Set([
		!view.canAddMeasure ? `A chart holds at most ${LIMITS.plainMeasures} measures.` : null,
		view.canCalculate && !view.canCalculate.offered ? view.canCalculate.reason : null,
		view.canAddFormula && !view.canAddFormula.offered ? view.canAddFormula.reason : null,
	].filter(Boolean))];
	return (
		<BuilderStep step='measures' number={2} title='Measure' {...step}>
			{view.measures.map((row) => {
				const m = draft.measures[row.index];
				const base = `chart-builder-m${row.index}`;
				const label = (m.label && m.label.trim()) || row.defaultLabel;
				const remove = many && row.removable !== false ? (
					<Button
						type='text'
						size='small'
						icon={<CLOSE />}
						className='chart-builder__remove'
						aria-label={`${row.kind === 'formula' ? 'Remove the formula' : 'Remove the measure'} ${label}`}
						onClick={() => onRemove(row.index)}
					/>
				) : null;
				if (row.keep) {
					return (
						<div className='chart-builder__measure chart-builder__measure--kept' key={row.key} data-measure={row.index}>
							<div className='chart-builder__measure-head'>
								<span className='chart-builder__kept-label'>{row.label}</span>
								{remove}
							</div>
							{row.formula ? (
								<>
									<div className='chart-builder__formula'>{row.words || row.formula}</div>
									<ShowAsText text={row.formula} label='Calculation as text' explain={false} />
								</>
							) : null}
							<div className='chart-builder__hint'>A calculation is kept as it is: the builder can't change it yet.</div>
							<FieldMessage messages={messagesFor(`measures.${row.index}`)} id={`${base}-messages`} />
						</div>
					);
				}
				const labelField = (
					<Field id={`${base}-label`} label='Label' messages={messagesFor(`measures.${row.index}.label`)}>
						<Input
							id={`${base}-label`}
							value={m.label}
							maxLength={LIMITS.labelLength}
							placeholder={row.defaultLabel}
							onChange={(event) => onMeasure(row.index, { label: event.target.value })}
						/>
					</Field>
				);
				if (row.kind === 'formula') {
					return (
						<div className='chart-builder__measure chart-builder__measure--formula' key={row.key} data-measure={row.index}>
							<div className='chart-builder__measure-head'>
								<span className='chart-builder__kept-label'>{label}</span>
								{remove}
							</div>
							<FormulaField
								id={`${base}-formula`}
								label={`Formula${ordinal(formulaRows, row)}`}
								value={m.formula}
								onChange={(formula) => onMeasure(row.index, { formula })}
								tokens={row.tokens}
								placeholder='Choose the measures above with the {{ }} button'
								hint={<>Pick the measures above with the <code>{'{{ }}'}</code> button; type operators between them.</>}
								messages={messagesFor(`measures.${row.index}.formula`)}
								status={row.status}
								clock={row.clock}
							/>
							{labelField}
							<FieldMessage messages={messagesFor(`measures.${row.index}`)} id={`${base}-messages`} />
						</div>
					);
				}
				const opMessages = messagesFor(`measures.${row.index}.op`);
				if (row.kind === 'perItem') {
					return (
						<div className='chart-builder__measure chart-builder__measure--calculated' key={row.key} data-measure={row.index}>
							<div className='chart-builder__measure-head'>
								<Select
									id={`${base}-op`}
									virtual={false}
									className='chart-builder__select chart-builder__verb'
									aria-label={many ? `Measure ${row.index + 1}` : 'Measure'}
									value={m.op}
									options={explainedOptions(row.verbs)}
									optionLabelProp='display'
									onChange={(op) => onMeasure(row.index, { op })}
									popupMatchSelectWidth={false}
									{...messageProps(opMessages, `${base}-op-messages`)}
								/>
								{remove}
							</div>
							<FieldMessage messages={opMessages} id={`${base}-op-messages`} />
							<FormulaField
								id={`${base}-perItem`}
								label={`Calculate per item${ordinal(perItemRows, row)}`}
								value={m.perItem}
								onChange={(perItem) => onMeasure(row.index, { perItem })}
								tokens={view.elementTokens}
								placeholder="Choose the card's elements with the {{ }} button"
								hint={<>Pick the card&apos;s elements with the <code>{'{{ }}'}</code> button; type operators between them.</>}
								messages={messagesFor(`measures.${row.index}.perItem`)}
								status={row.status}
								clock={row.clock}
							/>
							{row.blanks ? (
								<SegmentedField
									id={`${base}-blanks`}
									label='When an input is blank'
									value={m.blanks}
									options={BLANKS_OPTIONS}
									onChange={(blanks) => onMeasure(row.index, { blanks })}
									messages={messagesFor(`measures.${row.index}.blanks`)}
									hint={m.blanks === 'zero' ? 'A blank number counts as 0, so it can pull an average down.' : 'The item is left out, and the chart says how many.'}
								/>
							) : null}
							{row.hideable ? <HiddenToggle id={`${base}-hidden`} m={m} index={row.index} onMeasure={onMeasure} messages={messagesFor(`measures.${row.index}.hidden`)} /> : null}
							{row.shownWhileUnread ? <div className='chart-builder__hint'>{SHOWN_WHILE_UNREAD}</div> : null}
							{labelField}
							<FieldMessage messages={messagesFor(`measures.${row.index}`)} id={`${base}-messages`} />
						</div>
					);
				}
				const elementMessages = messagesFor(`measures.${row.index}.element`);
				const rowMessages = messagesFor(`measures.${row.index}`);
				return (
					<div className='chart-builder__measure' key={row.key} data-measure={row.index}>
						<div className='chart-builder__measure-head'>
							<Select
								id={`${base}-op`}
								virtual={false}
								className='chart-builder__select chart-builder__verb'
								aria-label={many ? `Measure ${row.index + 1}` : 'Measure'}
								value={m.op}
								options={explainedOptions(row.verbs)}
								optionLabelProp='display'
								onChange={(op) => onMeasure(row.index, { op })}
								popupMatchSelectWidth={false}
								{...messageProps(opMessages, `${base}-op-messages`)}
							/>
							{remove}
						</div>
						<FieldMessage messages={opMessages} id={`${base}-op-messages`} />
						{m.op !== 'count' ? (
							<Field id={`${base}-element`} messages={elementMessages}>
								<Select
									id={`${base}-element`}
									virtual={false}
									className='chart-builder__select'
									aria-label='Element'
									placeholder='Choose an element'
									value={m.element || undefined}
									options={explainedOptions(row.elements)}
									optionLabelProp='display'
									onChange={(element) => onMeasure(row.index, { element })}
									showSearch
									filterOption={optionSearchFilter}
									popupMatchSelectWidth={false}
									{...messageProps(elementMessages, `${base}-element-messages`)}
								/>
							</Field>
						) : null}
						{row.blanks ? (
							<SegmentedField
								id={`${base}-blanks`}
								label={`Items with no ${row.blanks.element}`}
								value={m.blanks}
								options={BLANKS_OPTIONS}
								onChange={(blanks) => onMeasure(row.index, { blanks })}
								messages={messagesFor(`measures.${row.index}.blanks`)}
								hint={m.blanks === 'zero' ? 'They count as zero, so they pull an average down.' : 'They are left out and the chart says how many.'}
							/>
						) : null}
						{row.unit ? (
							<Field
								id={`${base}-unit`}
								label='Show in'
								messages={messagesFor(`measures.${row.index}.unit`)}
								hint={row.unit.perUnit ? 'Totals count only the values in this unit; the rest are noted, not added.' : null}
							>
								<Select
									id={`${base}-unit`}
									virtual={false}
									className='chart-builder__select'
									value={m.unit || row.unit.defaultUnit || undefined}
									options={chartUnitOptions(row.unit.measure)}
									onChange={(unit) => onMeasure(row.index, { unit: unit === row.unit.defaultUnit ? null : unit })}
									showSearch
									filterOption={optionSearchFilter}
									popupMatchSelectWidth={false}
								/>
							</Field>
						) : null}
						{row.currency ? (
							<Field
								id={`${base}-currency`}
								label='Currency'
								messages={messagesFor(`measures.${row.index}.currency`)}
								hint='Amounts in another currency are never added in; the chart says how many there are.'
							>
								<Select
									id={`${base}-currency`}
									className='chart-builder__select'
									value={m.currency || row.currency.defaultCode || undefined}
									placeholder='Choose a currency'
									options={currencyOptions(row.currency.codes)}
									onChange={(currency) => onMeasure(row.index, { currency: currency === row.currency.defaultCode ? null : currency })}
									showSearch
									filterOption={optionSearchFilter}
									popupMatchSelectWidth={false}
								/>
							</Field>
						) : null}
						{row.hideable ? <HiddenToggle id={`${base}-hidden`} m={m} index={row.index} onMeasure={onMeasure} messages={messagesFor(`measures.${row.index}.hidden`)} /> : null}
						{row.shownWhileUnread ? <div className='chart-builder__hint'>{SHOWN_WHILE_UNREAD}</div> : null}
						{labelField}
						<FieldMessage messages={rowMessages} id={`${base}-messages`} />
					</div>
				);
			})}
			<div className='chart-builder__add'>
				<Button type='text' size='small' icon={<PLUS />} onClick={onAdd} disabled={!view.canAddMeasure} className='chart-builder__add-button'>
					Add measure
				</Button>
				<Button type='text' size='small' icon={<CALCULATOR />} onClick={onCalculate} disabled={!(view.canCalculate && view.canCalculate.offered)} className='chart-builder__add-button'>
					Calculate per item…
				</Button>
				<Button type='text' size='small' icon={<MATH_FUNCTION />} onClick={onAddFormula} disabled={!(view.canAddFormula && view.canAddFormula.offered)} className='chart-builder__add-button'>
					Add formula measure
				</Button>
			</div>
			{footReasons.map((reason) => <div key={reason} className='chart-builder__hint'>{reason}</div>)}
			{perItemRows.length || formulaRows.length ? (
				<Collapse
					size='small'
					className='chart-builder__reference'
					items={[{ key: 'reference', label: 'Formula reference', children: <FormulaReference /> }]}
				/>
			) : null}
		</BuilderStep>
	);
}
